import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress,
  MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { CloseRounded as RemoveIcon, UploadFileOutlined as UploadIcon } from '@mui/icons-material';
import DocumentService, { DocumentOwnerType, UploadableType } from '../../api/services/document.service';
import { ACCEPT, fileProblem } from './UploadDocumentDialog';
import { matchOwner, OwnerCandidate } from './matchOwners';

// Filing one kind of paper for many people at once — every driver's I-9,
// every truck's registration — instead of fifty trips through the upload
// dialog.
//
// Pick the kind, drop the files, and each file is matched to its person
// from its name. The table is the check: a sure match is just there, a
// guess says "Check", nobody found says so, and every row's person can be
// changed. Nothing uploads until "File N"; each file then goes on its own,
// so one bad scan does not stop the rest, and a failed row stays to retry.

const CONCURRENCY = 3;

interface Row {
  key: string;
  file: File;
  ownerId: string;
  sure: boolean;
  issuedAt: string;
  expiresAt: string;
  problem: string | null;
  state: 'ready' | 'uploading' | 'done' | 'failed';
  error?: string;
}

const BulkUploadDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onUploaded: () => void;
  ownerType: DocumentOwnerType;
  /** The per-owner folder whose people / trucks are listed, e.g. 'people'. */
  ownerFolderKey: string;
  presetTypeKey?: string;
}> = ({
  open, onClose, onUploaded, ownerType, ownerFolderKey, presetTypeKey,
}) => {
  const input = useRef<HTMLInputElement>(null);
  const [types, setTypes] = useState<UploadableType[]>([]);
  const [owners, setOwners] = useState<OwnerCandidate[]>([]);
  const [typeKey, setTypeKey] = useState(presetTypeKey || '');
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTypeKey(presetTypeKey || ''); setRows([]); setError(null);
    DocumentService.getUploadableTypes()
      .then((all) => setTypes(all.filter((t) => t.ownerType === ownerType)))
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load the document types'));
    DocumentService.getFolderOwners(ownerFolderKey)
      .then((list) => setOwners(list.map((o) => ({ ownerId: o.ownerId, label: o.label }))))
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load the people'));
  }, [open, ownerType, ownerFolderKey, presetTypeKey]);

  const type = types.find((t) => t.key === typeKey) || null;
  const expiryRequired = type?.expiry?.kind === 'continuous';
  const ownerWord = ownerType === 'vehicle' ? 'truck' : 'person';

  const addFiles = (list: FileList | null) => {
    const picked = Array.from(list || []);
    if (!picked.length) return;
    setRows((current) => [
      ...current,
      ...picked.map((file, i) => {
        const match = matchOwner(file.name, owners);
        return {
          key: `${Date.now()}-${i}-${file.name}`,
          file,
          ownerId: match.owner?.ownerId || '',
          sure: match.sure,
          issuedAt: '',
          expiresAt: '',
          problem: fileProblem(file),
          state: 'ready' as const,
        };
      }),
    ]);
    if (input.current) input.current.value = '';
  };

  const patch = (key: string, change: Partial<Row>) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...change } : r)));
  const fillAll = (field: 'issuedAt' | 'expiresAt', value: string) => setRows((rs) => rs.map((r) => (r.state === 'done' ? r : { ...r, [field]: value })));

  // The same person twice is usually two pages of one form, or a typo in a
  // file name — worth a look, not a block.
  const twice = useMemo(() => {
    const seen = new Map<string, number>();
    rows.forEach((r) => r.ownerId && seen.set(r.ownerId, (seen.get(r.ownerId) || 0) + 1));
    return new Set(Array.from(seen.entries()).filter(([, n]) => n > 1).map(([id]) => id));
  }, [rows]);

  const pending = rows.filter((r) => r.state !== 'done');
  const ready = pending.filter((r) => r.ownerId && !r.problem && (!expiryRequired || r.expiresAt));
  const done = rows.filter((r) => r.state === 'done').length;

  const upload = async () => {
    if (!type) return;
    setBusy(true); setError(null);
    const queue = [...ready];
    const worker = async () => {
      while (queue.length) {
        const row = queue.shift()!;
        patch(row.key, { state: 'uploading', error: undefined });
        try {
          await DocumentService.upload({
            documentTypeKey: type.key,
            ownerId: row.ownerId,
            ownerLabel: owners.find((o) => o.ownerId === row.ownerId)?.label,
            issuedAt: row.issuedAt ? new Date(row.issuedAt).toISOString() : undefined,
            expiresAt: row.expiresAt ? new Date(row.expiresAt).toISOString() : undefined,
            file: row.file,
          });
          patch(row.key, { state: 'done' });
        } catch (err) {
          patch(row.key, { state: 'failed', error: err instanceof Error ? err.message : 'Could not be filed' });
        }
      }
    };
    await Promise.all(Array.from({ length: CONCURRENCY }, worker));
    setBusy(false);
    onUploaded();
  };

  const allDone = rows.length > 0 && done === rows.length;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>File many at once</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} sx={{ mt: 1, mb: 2 }} alignItems={{ sm: 'center' }}>
          <TextField
            select size="small" label="What these are" value={typeKey} onChange={(e) => setTypeKey(e.target.value)}
            sx={{ minWidth: 280 }} disabled={busy}
          >
            {types.map((t) => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
          </TextField>
          <input ref={input} type="file" multiple hidden accept={ACCEPT} onChange={(e) => addFiles(e.target.files)} />
          <Button variant="outlined" startIcon={<UploadIcon />} disabled={!type || busy || !owners.length} onClick={() => input.current?.click()}>
            {rows.length ? 'Add more files' : 'Choose files'}
          </Button>
          <Typography variant="caption" color="text.secondary">
            Each file is matched to its {ownerWord} by its name. Check the table, then file them all.
          </Typography>
        </Stack>

        {type && rows.length === 0 && (
          <Box
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); if (!busy) addFiles(e.dataTransfer.files); }}
            sx={{ border: '2px dashed', borderColor: 'primary.light', borderRadius: 3, p: 5, textAlign: 'center', bgcolor: 'action.hover' }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Drop the {type.label} files here</Typography>
            <Typography variant="caption" color="text.secondary">Name them with the {ownerWord}&apos;s name — &quot;Dana Reyes I-9.pdf&quot; — and they match themselves.</Typography>
          </Box>
        )}

        {rows.length > 0 && (
          <>
            <TableContainer sx={{ maxHeight: '55vh', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
              onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); if (!busy) addFiles(e.dataTransfer.files); }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>File</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>{ownerType === 'vehicle' ? 'Truck' : 'Person'}</TableCell>
                    <TableCell>Issued</TableCell>
                    {type?.expiry && <TableCell>Expires{expiryRequired ? ' *' : ''}</TableCell>}
                    <TableCell />
                  </TableRow>
                  <TableRow>
                    {/* One date for the whole batch, then adjust the odd one. */}
                    <TableCell colSpan={2} sx={{ top: 37, color: 'text.secondary', fontSize: 12 }}>Same date for every row →</TableCell>
                    <TableCell sx={{ top: 37 }}>
                      <TextField size="small" type="date" disabled={busy} inputProps={{ 'aria-label': 'Issued for all' }}
                        onChange={(e) => fillAll('issuedAt', e.target.value)} />
                    </TableCell>
                    {type?.expiry && (
                      <TableCell sx={{ top: 37 }}>
                        <TextField size="small" type="date" disabled={busy} inputProps={{ 'aria-label': 'Expires for all' }}
                          onChange={(e) => fillAll('expiresAt', e.target.value)} />
                      </TableCell>
                    )}
                    <TableCell sx={{ top: 37 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.key} sx={{ opacity: row.state === 'done' ? 0.55 : 1 }}>
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Typography variant="body2" noWrap title={row.file.name}>{row.file.name}</Typography>
                        {row.problem && <Typography variant="caption" color="error.main">{row.problem}</Typography>}
                        {row.error && <Typography variant="caption" color="error.main">{row.error}</Typography>}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" gap={1} alignItems="center">
                          <TextField
                            select size="small" fullWidth value={row.ownerId} disabled={busy || row.state === 'done'}
                            onChange={(e) => patch(row.key, { ownerId: e.target.value, sure: true })}
                            SelectProps={{ displayEmpty: true }}
                            inputProps={{ 'aria-label': `${ownerWord} for ${row.file.name}` }}
                            error={!row.ownerId}
                          >
                            <MenuItem value=""><em>Who is this?</em></MenuItem>
                            {owners.map((o) => <MenuItem key={o.ownerId} value={o.ownerId}>{o.label}</MenuItem>)}
                          </TextField>
                          {row.ownerId && !row.sure && row.state !== 'done' && <Chip size="small" color="warning" label="Check" />}
                          {row.ownerId && twice.has(row.ownerId) && row.state !== 'done' && <Chip size="small" label="Twice" />}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <TextField size="small" type="date" value={row.issuedAt} disabled={busy || row.state === 'done'}
                          inputProps={{ 'aria-label': `Issued ${row.file.name}` }}
                          onChange={(e) => patch(row.key, { issuedAt: e.target.value })} />
                      </TableCell>
                      {type?.expiry && (
                        <TableCell>
                          <TextField size="small" type="date" value={row.expiresAt} disabled={busy || row.state === 'done'}
                            error={expiryRequired && !row.expiresAt}
                            inputProps={{ 'aria-label': `Expires ${row.file.name}` }}
                            onChange={(e) => patch(row.key, { expiresAt: e.target.value })} />
                        </TableCell>
                      )}
                      <TableCell align="right">
                        {row.state === 'done' ? <Chip size="small" color="success" label="Filed" />
                          : row.state === 'uploading' ? <Chip size="small" label="Filing…" />
                            : (
                              <IconButton size="small" aria-label={`Remove ${row.file.name}`} disabled={busy}
                                onClick={() => setRows((rs) => rs.filter((r) => r.key !== row.key))}>
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                            )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {busy && <LinearProgress variant="determinate" value={(done / Math.max(1, rows.length)) * 100} sx={{ mt: 1.5 }} />}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {rows.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
            {done ? `${done} filed · ` : ''}
            {ready.length} ready
            {pending.length - ready.length > 0 && ` · ${pending.length - ready.length} need a ${ownerWord}${expiryRequired ? ' or an expiry date' : ''}`}
          </Typography>
        )}
        <Button onClick={onClose} disabled={busy}>{allDone ? 'Close' : 'Cancel'}</Button>
        {!allDone && (
          <Button variant="contained" disableElevation onClick={upload} disabled={busy || !ready.length}>
            {busy ? 'Filing…' : `File ${ready.length || ''}`.trim()}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkUploadDialog;
